/**
 * Browser side of the Alloy passkey factor (ALL-5747, specs/alloy-mfa S-5).
 *
 * The auth service (Yubico java-webauthn-server) serves the options as the
 * standard WebAuthn JSON `{"publicKey": {...}}`, with every binary field as
 * base64url, and parses the standard JSON of the resulting PublicKeyCredential.
 * These helpers only convert between that JSON and the ArrayBuffers
 * `navigator.credentials` takes and returns. Nothing here is stored.
 */

type Json = Record<string, unknown>;

export function base64urlToBuffer(value: string): ArrayBuffer {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function bufferToBase64url(buffer: ArrayBuffer | ArrayBufferView): string {
  const bytes = ArrayBuffer.isView(buffer)
    ? new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength)
    : new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function publicKeyOf(options: unknown): Json {
  if (!options || typeof options !== "object") throw new Error("webauthn options are malformed");
  const wrapped = (options as Json).publicKey;
  const publicKey = wrapped && typeof wrapped === "object" ? (wrapped as Json) : (options as Json);
  if (typeof publicKey.challenge !== "string") throw new Error("webauthn options carry no challenge");
  return publicKey;
}

function credentialDescriptors(value: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .filter((entry): entry is Json => !!entry && typeof entry === "object" && typeof (entry as Json).id === "string")
    .map((entry) => ({
      ...(entry as object),
      type: "public-key",
      id: base64urlToBuffer(entry.id as string),
    })) as PublicKeyCredentialDescriptor[];
}

/** `{"publicKey": ...}` from POST /v1/mfa/webauthn/registration-options, as navigator.credentials.create takes it. */
export function toCreationOptions(options: unknown): PublicKeyCredentialCreationOptions {
  const publicKey = publicKeyOf(options);
  const user = publicKey.user as Json | undefined;
  if (!user || typeof user.id !== "string") throw new Error("webauthn options carry no user handle");
  return {
    ...(publicKey as object),
    challenge: base64urlToBuffer(publicKey.challenge as string),
    user: { ...(user as object), id: base64urlToBuffer(user.id) } as PublicKeyCredentialUserEntity,
    excludeCredentials: credentialDescriptors(publicKey.excludeCredentials),
  } as PublicKeyCredentialCreationOptions;
}

/** `{"publicKey": ...}` from POST /v1/mfa/webauthn/assertion-options, as navigator.credentials.get takes it. */
export function toRequestOptions(options: unknown): PublicKeyCredentialRequestOptions {
  const publicKey = publicKeyOf(options);
  return {
    ...(publicKey as object),
    challenge: base64urlToBuffer(publicKey.challenge as string),
    allowCredentials: credentialDescriptors(publicKey.allowCredentials),
  } as PublicKeyCredentialRequestOptions;
}

function extensionResults(credential: PublicKeyCredential): Json {
  try {
    return (credential.getClientExtensionResults?.() ?? {}) as Json;
  } catch {
    return {};
  }
}

/** A registration PublicKeyCredential as the standard JSON the auth service parses. */
export function registrationToJson(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAttestationResponse;
  const transports = (response as { getTransports?: () => string[] }).getTransports?.();
  return JSON.stringify({
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      attestationObject: bufferToBase64url(response.attestationObject),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      ...(Array.isArray(transports) ? { transports } : {}),
    },
    clientExtensionResults: extensionResults(credential),
  });
}

/** An assertion PublicKeyCredential as the standard JSON the auth service parses. */
export function assertionToJson(credential: PublicKeyCredential): string {
  const response = credential.response as AuthenticatorAssertionResponse;
  return JSON.stringify({
    type: credential.type,
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      authenticatorData: bufferToBase64url(response.authenticatorData),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      signature: bufferToBase64url(response.signature),
      ...(response.userHandle ? { userHandle: bufferToBase64url(response.userHandle) } : {}),
    },
    clientExtensionResults: extensionResults(credential),
  });
}

/** True when this browser exposes the WebAuthn API at all. */
export function passkeysSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.credentials &&
    typeof navigator.credentials.create === "function" &&
    typeof navigator.credentials.get === "function"
  );
}
